(() => {
  if (!window.limencyPillowPreviewSettings) return;

  const DESIGN_SIZE = 1200;
  const PHOTO_BOXES = [
    { x: 65, y: 96, w: 500, h: 710 },
    { x: 635, y: 96, w: 500, h: 710 },
  ];
  const settings = window.limencyPillowPreviewSettings || {};
  const FABRIC_EDGE = Number(settings.fabricBorder ?? 8);
  const ROUNDING = Number(settings.smoothing ?? 11);
  const BG_TOLERANCE = Number(settings.backgroundTolerance ?? 8);
  let overlay;
  let lastHash = 0;
  let scheduled = false;

  function ensureOverlay(base) {
    const frame = base.parentElement;
    if (!frame) return null;
    if (!overlay || overlay.parentElement !== frame) {
      overlay?.remove();
      overlay = document.createElement('canvas');
      overlay.className = 'bonaper-pillow-edge-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.style.cssText = 'position:absolute;pointer-events:none;z-index:2;';
      frame.append(overlay);
      lastHash = 0;
    }
    if (overlay.width !== base.width || overlay.height !== base.height) {
      overlay.width = base.width;
      overlay.height = base.height;
      lastHash = 0;
    }
    const box = base.getBoundingClientRect();
    const parent = frame.getBoundingClientRect();
    overlay.style.left = `${box.left - parent.left}px`;
    overlay.style.top = `${box.top - parent.top}px`;
    overlay.style.width = `${box.width}px`;
    overlay.style.height = `${box.height}px`;
    return overlay;
  }

  function boxesFor(width, height) {
    return PHOTO_BOXES.map((box) => ({
      x0: Math.max(0, Math.floor(box.x * width / DESIGN_SIZE)),
      y0: Math.max(0, Math.floor(box.y * height / DESIGN_SIZE)),
      x1: Math.min(width, Math.ceil((box.x + box.w) * width / DESIGN_SIZE)),
      y1: Math.min(height, Math.ceil((box.y + box.h) * height / DESIGN_SIZE)),
    }));
  }

  function fillHoles(alpha, width, box) {
    const bw = box.x1 - box.x0;
    const bh = box.y1 - box.y0;
    const seen = new Uint8Array(bw * bh);
    const queue = new Int32Array(bw * bh);
    let end = 0;
    const add = (x, y) => {
      if (x < 0 || y < 0 || x >= bw || y >= bh) return;
      const local = y * bw + x;
      if (seen[local] || alpha[(box.y0 + y) * width + box.x0 + x]) return;
      seen[local] = 1;
      queue[end++] = local;
    };
    for (let x = 0; x < bw; x++) { add(x, 0); add(x, bh - 1); }
    for (let y = 0; y < bh; y++) { add(0, y); add(bw - 1, y); }
    for (let start = 0; start < end; start++) {
      const p = queue[start];
      const x = p % bw;
      const y = (p - x) / bw;
      add(x - 1, y); add(x + 1, y); add(x, y - 1); add(x, y + 1);
    }
    for (let y = 0; y < bh; y++) for (let x = 0; x < bw; x++) {
      if (!seen[y * bw + x]) alpha[(box.y0 + y) * width + box.x0 + x] = 255;
    }
  }

  function captureMask(base, previousHash) {
    const width = base.width;
    const height = base.height;
    if (width < 100 || height < 100) return null;
    const pixels = base.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, width, height).data;
    const bg = [pixels[0], pixels[1], pixels[2]];
    const alpha = new Uint8Array(width * height);
    let count = 0;
    let hash = 2166136261;
    const boxes = boxesFor(width, height);
    for (const box of boxes) for (let y = box.y0; y < box.y1; y++) for (let x = box.x0; x < box.x1; x++) {
      const p = y * width + x;
      const i = p * 4;
      const difference = Math.max(
        Math.abs(pixels[i] - bg[0]),
        Math.abs(pixels[i + 1] - bg[1]),
        Math.abs(pixels[i + 2] - bg[2]),
      );
      if (pixels[i + 3] > 100 && difference > BG_TOLERANCE * .625) { alpha[p] = 255; count++; }
      if ((x & 7) === 0 && (y & 7) === 0) {
        hash = Math.imul(hash ^ pixels[i] ^ (pixels[i + 1] << 8) ^ (pixels[i + 2] << 16), 16777619);
      }
    }
    if (count < width * height * .004) return null;
    if (hash === previousHash) return { mask: null, hash };
    const raw = document.createElement('canvas');
    raw.width = width;
    raw.height = height;
    const rawCtx = raw.getContext('2d');
    const rawData = rawCtx.createImageData(width, height);
    for (let p = 0; p < alpha.length; p++) {
      rawData.data[p * 4] = 255;
      rawData.data[p * 4 + 1] = 255;
      rawData.data[p * 4 + 2] = 255;
      rawData.data[p * 4 + 3] = alpha[p];
    }
    rawCtx.putImageData(rawData, 0, 0);

    const rounded = document.createElement('canvas');
    rounded.width = width;
    rounded.height = height;
    const roundedCtx = rounded.getContext('2d', { willReadFrequently: true });
    roundedCtx.filter = `blur(${ROUNDING * width / DESIGN_SIZE}px)`;
    roundedCtx.drawImage(raw, 0, 0);
    roundedCtx.filter = 'none';
    const soft = roundedCtx.getImageData(0, 0, width, height);
    const smoothAlpha = new Uint8Array(width * height);
    for (let p = 0; p < smoothAlpha.length; p++) {
      if (soft.data[p * 4 + 3] > 72) smoothAlpha[p] = 255;
    }
    boxes.forEach((box) => fillHoles(smoothAlpha, width, box));
    for (let p = 0; p < smoothAlpha.length; p++) soft.data[p * 4 + 3] = smoothAlpha[p];
    roundedCtx.putImageData(soft, 0, 0);
    return { mask: rounded, hash };
  }

  function paint(base, mask) {
    const target = ensureOverlay(base);
    if (!target) return;
    const width = target.width;
    const height = target.height;
    const shell = document.createElement('canvas');
    shell.width = width;
    shell.height = height;
    const shellCtx = shell.getContext('2d');
    const radius = FABRIC_EDGE * width / DESIGN_SIZE;
    for (let step = 0; step < 32; step++) {
      const angle = step * Math.PI / 16;
      shellCtx.drawImage(mask, Math.cos(angle) * radius, Math.sin(angle) * radius);
    }
    shellCtx.globalCompositeOperation = 'source-in';
    shellCtx.fillStyle = '#fffdf9';
    shellCtx.fillRect(0, 0, width, height);

    const ctx = target.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.save();
    ctx.shadowColor = 'rgba(67, 57, 48, .22)';
    ctx.shadowBlur = 18 * width / DESIGN_SIZE;
    ctx.shadowOffsetY = 10 * width / DESIGN_SIZE;
    ctx.drawImage(shell, 0, 0);
    ctx.restore();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(mask, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    target.hidden = false;
  }

  function update() {
    const base = document.querySelector('[data-bonaper-canvas]');
    const thumb = document.querySelector('.bonaper-field[data-bonaper-field="pet-photo"] .bonaper-photo__thumb:not([hidden])');
    if (!base || !(thumb instanceof HTMLImageElement) || !thumb.complete || !thumb.naturalWidth) {
      if (overlay) overlay.hidden = true;
      lastHash = 0;
      return;
    }
    const target = ensureOverlay(base);
    if (!target) return;
    try {
      const result = captureMask(base, target.hidden ? 0 : lastHash);
      if (!result) { target.hidden = true; lastHash = 0; return; }
      if (result.mask) {
        paint(base, result.mask);
        lastHash = result.hash;
      }
    } catch {
      target.hidden = true;
      lastHash = 0;
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; update(); });
  }

  new MutationObserver(schedule).observe(document.body, {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['src', 'hidden'],
  });
  document.addEventListener('load', schedule, true);
  window.addEventListener('resize', schedule);
  setInterval(schedule, 700);
  schedule();
})();
